import { Component, OnInit, OnDestroy, HostListener, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { NotificationService, Notification } from '../../../services/notification.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-notification-bell',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatBadgeModule, MatButtonModule],
  templateUrl: './notification-bell.component.html',
  styleUrls: ['./notification-bell.component.scss']
})
export class NotificationBellComponent implements OnInit, OnDestroy {
  unreadCount = 0;
  notifications: Notification[] = [];
  showDropdown = false;
  isMarkingAllAsRead = false;
  private subscriptions = new Subscription();
  
  constructor(
    private notificationService: NotificationService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}
  
  ngOnInit(): void {
    // Subscribe to unread count and store subscription
    this.subscriptions.add(
      this.notificationService.getUnreadCount().subscribe(count => {
        this.unreadCount = count;
        this.cdr.detectChanges(); // Trigger change detection
      })
    );
    
    // Subscribe to notifications and store subscription
    this.subscriptions.add(
      this.notificationService.notifications$.subscribe(notifications => {
        this.notifications = notifications;
        this.cdr.detectChanges(); // Trigger change detection
      })
    );
  }
  
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions to prevent memory leaks
    this.subscriptions.unsubscribe();
  }
  
  /**
   * Toggle dropdown visibility
   */
  toggleDropdown(): void {
    this.showDropdown = !this.showDropdown;
    
    if (this.showDropdown) {
      // Fetch notifications when opening dropdown
      this.notificationService.fetchNotifications(1, 20, false);
    }
  }
  
  /**
   * Handle notification click
   */
  async onNotificationClick(notification: Notification): Promise<void> {
    // Mark as read
    if (!notification.is_read) {
      this.subscriptions.add(
        this.notificationService.markAsRead(notification.id).subscribe({
          next: () => {
            // Update local state
            notification.is_read = true;
            // Refresh notifications (this will also update unread count)
            this.notificationService.fetchNotifications(1, 20, false);
            this.cdr.detectChanges(); // Trigger change detection
          },
          error: (error) => {
            console.error('Error marking notification as read:', error);
          }
        })
      );
    }
    
    // Navigate to analysis page if projectdata_id is available
    if (notification.projectdata_id && notification.project_id) {
      this.router.navigate([`/common-ai-analysis/generic/${notification.project_id}`], {
        queryParams: { projectdataId: notification.projectdata_id }
      });
      this.showDropdown = false;
    }
  }
  
  /**
   * Close dropdown when clicking outside
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.notification-bell-container')) {
      this.showDropdown = false;
    }
  }
  
  /**
   * Mark all notifications as read
   */
  markAllAsRead(): void {
    if (this.isMarkingAllAsRead || this.unreadCount === 0) {
      return;
    }
    
    this.isMarkingAllAsRead = true;
    
    this.subscriptions.add(
      this.notificationService.markAllAsRead().subscribe({
        next: () => {
          // Update all local notifications to read
          this.notifications.forEach(notification => {
            notification.is_read = true;
          });
          
          // Reset unread count
          this.unreadCount = 0;
          
          // Refresh notifications list to sync with server
          this.notificationService.fetchNotifications(1, 20, false);
          
          this.cdr.detectChanges();
          this.isMarkingAllAsRead = false;
        },
        error: (error) => {
          console.error('Error marking all notifications as read:', error);
          this.isMarkingAllAsRead = false;
          this.cdr.detectChanges();
        }
      })
    );
  }
  
  /**
   * Format timestamp for display
   */
  formatTimestamp(timestamp: string): string {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) {
      return 'Just now';
    } else if (diffMins < 60) {
      return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  }
}
